// Intent: the "Twój miesiąc" ring, redrawn Napper-style (owner walkthrough) as
// an OPEN arc — a broad band with a gap at the bottom (not a full circle, so it
// isn't too tall). Along the band sit finger-sized bubbles with an icon per
// event — green = expected income, orange = fixed cost — plus a small day number
// so you can eyeball WHEN money comes and goes. The navy dot is today; the arc
// fills up to today. The centre is an overlay passed as children.

import type { ReactNode } from 'react';
import { Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Svg, { Circle, Path } from 'react-native-svg';

import { AppText } from '@/components/ui/app-text';
import { colors } from '@/theme/tokens';
import type { MonthMarker } from '@/utils/forecast';

// Open ring geometry: a 300° arc with a 60° gap centred on the bottom. Angles
// are measured from the top (0°), clockwise; the arc runs the long way from the
// bottom-left (210°) up over the top and back to the bottom-right (150°+360°).
const GAP = 66;
const START = 180 + GAP / 2; // 213° — bottom-left
const SWEEP = 360 - GAP; // 294° drawn span
const INCOME = '#3BB273';
const COST = '#F08C2E';
const BUBBLE = 34; // event bubble diameter

// 0° at the top, clockwise (SVG y grows downwards → subtract 90° from the angle).
function polar(cx: number, cy: number, r: number, deg: number) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

// The open arc as an SVG path, sampled as line segments so there is never any
// large-arc/sweep-flag ambiguity. f0..f1 are fractions (0 = arc start).
function arcPath(cx: number, cy: number, r: number, f0: number, f1: number, steps = 60): string {
  const pts: string[] = [];
  for (let i = 0; i <= steps; i += 1) {
    const f = f0 + ((f1 - f0) * i) / steps;
    const p = polar(cx, cy, r, START + f * SWEEP);
    pts.push(`${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`);
  }
  return pts.join(' ');
}

export function MonthRing({
  markers,
  today,
  daysInMonth,
  size = 220,
  stroke = 18,
  children,
  onMarkerPress,
}: {
  markers: MonthMarker[];
  today: number;
  daysInMonth: number;
  size?: number;
  /** Band thickness — owner wanted it about as wide as the event icons. */
  stroke?: number;
  children?: ReactNode;
  /** Tap a bubble to reveal what it is (owner: "nie mogę kliknąć w kropkę"). */
  onMarkerPress?: (m: MonthMarker) => void;
}) {
  const cx = size / 2;
  const cy = size / 2;
  // Leave room for the bubbles + day labels that sit on/outside the band.
  const r = size / 2 - BUBBLE / 2 - 14;
  const span = Math.max(1, daysInMonth - 1);
  const elapsed = Math.min(1, Math.max(0, (today - 1) / span));
  const fracOf = (day: number) => (Math.min(Math.max(day, 1), daysInMonth) - 1) / span;
  const todayPos = polar(cx, cy, r, START + elapsed * SWEEP);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size} style={{ position: 'absolute' }}>
        {/* Track (whole month) */}
        <Path d={arcPath(cx, cy, r, 0, 1)} stroke={colors.primaryTile} strokeWidth={stroke} strokeLinecap="round" fill="none" />
        {/* Elapsed progress up to today */}
        {elapsed > 0 && (
          <Path
            d={arcPath(cx, cy, r, 0, elapsed)}
            stroke={colors.primary}
            strokeWidth={stroke}
            strokeLinecap="round"
            fill="none"
          />
        )}
        {/* Today marker — scales with the (now thicker) band */}
        <Circle cx={todayPos.x} cy={todayPos.y} r={stroke * 0.5} fill="#FFFFFF" stroke={colors.navy} strokeWidth={4} />
      </Svg>

      {/* Event bubbles + day labels — RN Views so we can use real Ionicons and
          keep a comfortable tap target on the band. */}
      {markers.map((m, i) => {
        const p = polar(cx, cy, r, START + fracOf(m.day) * SWEEP);
        const lp = polar(cx, cy, r + BUBBLE / 2 + 9, START + fracOf(m.day) * SWEEP);
        // Overdue & unanswered = greyed (the plan is honest that it's waiting
        // for the user's answer, not pretending the event happened).
        const tint = m.pending ? colors.outlineVariant : m.kind === 'INCOME' ? INCOME : COST;
        return (
          <View key={`m-${i}`} pointerEvents="box-none" style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0 }}>
            {/* Day number, just outside the band */}
            <View style={{ position: 'absolute', left: lp.x - 16, top: lp.y - 9, width: 32, alignItems: 'center' }}>
              <AppText variant="labelMd" color={colors.textSecondary}>
                {m.day}.
              </AppText>
            </View>
            {/* Icon bubble */}
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={m.label}
              disabled={!onMarkerPress}
              onPress={() => onMarkerPress?.(m)}
              hitSlop={6}
              style={{
                position: 'absolute',
                left: p.x - BUBBLE / 2,
                top: p.y - BUBBLE / 2,
                width: BUBBLE,
                height: BUBBLE,
                borderRadius: BUBBLE / 2,
                backgroundColor: '#FFFFFF',
                borderWidth: 2.5,
                borderColor: tint,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Ionicons name={m.icon} size={17} color={tint} />
            </Pressable>
          </View>
        );
      })}

      {/* Center overlay */}
      <View style={{ alignItems: 'center', justifyContent: 'center', paddingHorizontal: 24 }}>{children}</View>
    </View>
  );
}
